import React from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation, gql } from '@apollo/client';
import OpportunityDetail from '../../components/StudentPlatform/pages/OpportunityDetail';
import ApolloWrapper from './ApolloWrapper';

const GET_OPPORTUNITY_DETAIL = gql`
  query GetOpportunityDetail($id: ID!) {
    currentUser {
      id
      name
      email
    }
    hiringPost(id: $id) {
      id
      slug
      title
      position_type
      location
      firm_name
      remote
      description
      requirements
      qualifications
      compensation
      duration
      start_date
      linkedin_url
      application_instructions
      published_at
      views_count
      applications_count
      status
      advisor_display_name
      user {
        id
        name
        allowed_name
        profile_picture
        headline
        years_practicing
        designations
        advisor_licenses
      }
    }
    getPersonalConversations {
      id
      participant1 {
        id
      }
      participant2 {
        id
      }
    }
  }
`;

const CREATE_OR_FIND_CONVERSATION = gql`
  mutation CreateOrFindStudentConversation($hiring_post_id: ID!) {
    createOrFindStudentConversation(hiring_post_id: $hiring_post_id) {
      conversation {
        id
      }
      success
      message
    }
  }
`;

const OpportunityDetailInner = ({
  opportunityId,
  brandLogoUrl,
  primaryColor,
  secondaryColor,
  accentColor,
  neutralColor,
}) => {
  const { loading, error, data } = useQuery(GET_OPPORTUNITY_DETAIL, {
    variables: { id: opportunityId },
  });
  
  const [createConversation] = useMutation(CREATE_OR_FIND_CONVERSATION);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading opportunity...</p>
        </div>
      </div>
    );
  }

  if (error || !data?.hiringPost) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error loading opportunity</p>
          <p className="text-gray-600 mt-2">
            {error?.message || 'Opportunity not found'}
          </p>
          <a
            href="/student/dashboard"
            className="mt-4 inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }

  // Check if student is already messaging with this advisor
  const conversations = data?.getPersonalConversations || [];
  const currentUserId = data?.currentUser?.id;
  const advisorId = data?.hiringPost?.user?.id;
  
  const isMessagingWithAdvisor = conversations.some(conv => {
    const participant1Id = conv.participant1?.id?.toString();
    const participant2Id = conv.participant2?.id?.toString();
    const currentUserIdStr = currentUserId?.toString();
    const advisorIdStr = advisorId?.toString();
    
    return (
      (participant1Id === currentUserIdStr && participant2Id === advisorIdStr) ||
      (participant2Id === currentUserIdStr && participant1Id === advisorIdStr)
    );
  });

  return (
    <OpportunityDetail
      currentUser={data?.currentUser}
      opportunity={data?.hiringPost}
      onSendMessage={createConversation}
      isMessagingWithAdvisor={isMessagingWithAdvisor}
      brandLogoUrl={brandLogoUrl}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
      accentColor={accentColor}
      neutralColor={neutralColor}
    />
  );
};

OpportunityDetailInner.propTypes = {
  opportunityId: PropTypes.string.isRequired,
  brandLogoUrl: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  accentColor: PropTypes.string,
  neutralColor: PropTypes.string,
};

const OpportunityDetailContainer = (props) => (
  <ApolloWrapper>
    <OpportunityDetailInner {...props} />
  </ApolloWrapper>
);

OpportunityDetailContainer.propTypes = {
  opportunityId: PropTypes.string.isRequired,
  brandLogoUrl: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  accentColor: PropTypes.string,
  neutralColor: PropTypes.string,
};

export default OpportunityDetailContainer;

